import { useCallback } from "react";
import { PartialCreateCampaignInquiryInput } from "src/components/CreateCampaignInquiryForm/utils";
import { FileInput } from "src/graphql/generated/graphql";

import { useDuplicateImage } from "./useDuplicateImage";

export const useDuplications = () => {
    const { duplicateImage } = useDuplicateImage();

    const createDuplicateImage = useCallback(
        async (id?: string) => {
            if (!id) {
                return;
            }

            const duplicatedImage = await duplicateImage(id);

            if (!duplicatedImage) {
                return;
            }

            return {
                id: duplicatedImage.id,
                objectKey: duplicatedImage.objectKey,
                src: duplicatedImage.src,
            };
        },
        [duplicateImage]
    );

    const createDuplicateCampaignImage = useCallback(
        async (id?: string) => {
            if (!id) {
                return;
            }

            const duplicatedImage = await duplicateImage(id);

            if (!duplicatedImage) {
                return;
            }

            return {
                file: {
                    id: duplicatedImage.id,
                    objectKey: duplicatedImage.objectKey,
                    src: duplicatedImage.src,
                },
            };
        },
        [duplicateImage]
    );

    const createDuplicateImages = useCallback(
        async (images?: FileInput[]) => {
            if (!images) {
                return;
            }

            const promises: Promise<FileInput>[] = [];

            images.forEach((image) => {
                promises.push(createDuplicateImage(image.id));
            });

            const duplicatedImagesSettled = await Promise.allSettled(promises);

            const duplicatedImages: FileInput[] = [];

            duplicatedImagesSettled.forEach((image) => {
                if (image.status === "fulfilled") {
                    duplicatedImages.push(image.value);
                }
            });

            return duplicatedImages.length ? duplicatedImages : undefined;
        },
        [createDuplicateImage]
    );

    const createDuplicateCampaignImages = useCallback(
        async (images?: PartialCreateCampaignInquiryInput["images"]) => {
            if (!images) {
                return;
            }

            const promises: Promise<PartialCreateCampaignInquiryInput["images"][number]>[] = [];

            images.forEach((image) => {
                promises.push(createDuplicateCampaignImage(image.file?.id));
            });

            const duplicatedImagesSettled = await Promise.allSettled(promises);

            const duplicatedImages: PartialCreateCampaignInquiryInput["images"] = [];

            duplicatedImagesSettled.forEach((image) => {
                if (image.status === "fulfilled") {
                    duplicatedImages.push(image.value);
                }
            });

            return duplicatedImages.length ? duplicatedImages : undefined;
        },
        [createDuplicateCampaignImage]
    );

    const createDuplicateProductImages = useCallback(
        async (product?: PartialCreateCampaignInquiryInput["products"][number]) => {
            if (!product) {
                return;
            }

            const [duplicatedThumbnailImageSettled, duplicatedImagesSettled, descriptionImagesSettled] =
                await Promise.allSettled([
                    createDuplicateImage(product.thumbnailImage?.id),
                    createDuplicateImages(product.images),
                    createDuplicateImages(product.description?.images),
                ]);

            let duplicatedThumbnailImage: FileInput | undefined;

            if (duplicatedThumbnailImageSettled.status === "fulfilled") {
                duplicatedThumbnailImage = duplicatedThumbnailImageSettled.value;
            }

            let duplicatedImages: FileInput[] | undefined = undefined;

            if (duplicatedImagesSettled.status === "fulfilled") {
                duplicatedImages = duplicatedImagesSettled.value;
            }

            let descriptionImages: FileInput[] | undefined = undefined;

            if (descriptionImagesSettled.status === "fulfilled") {
                descriptionImages = descriptionImagesSettled.value;
            }

            return {
                ...product,
                thumbnailImage: duplicatedThumbnailImage,
                images: duplicatedImages,
                description: {
                    ...product.description,
                    images: descriptionImages,
                },
            };
        },
        [createDuplicateImage, createDuplicateImages]
    );

    const createDuplicateProductsImages = useCallback(
        async (products?: PartialCreateCampaignInquiryInput["products"]) => {
            if (!products) {
                return;
            }

            const promises: Promise<PartialCreateCampaignInquiryInput["products"][number]>[] = [];

            products.forEach((product) => {
                promises.push(createDuplicateProductImages(product));
            });

            const productsSettled = await Promise.allSettled(promises);

            const newProducts: PartialCreateCampaignInquiryInput["products"] = [];

            productsSettled.forEach((product) => {
                if (product.status === "fulfilled") {
                    newProducts.push(product.value);
                }
            });

            return newProducts.length ? newProducts : undefined;
        },
        [createDuplicateProductImages]
    );

    const createDuplicateCampaignInquiryImages = useCallback(
        async (locationData: PartialCreateCampaignInquiryInput) => {
            const [thumbnailImage, descriptionImages, images, products] = await Promise.allSettled([
                createDuplicateImage(locationData?.thumbnailImage?.id),
                createDuplicateImages(locationData?.description?.images),
                createDuplicateCampaignImages(locationData?.images),
                createDuplicateProductsImages(locationData?.products),
            ]);

            let newDescriptionImages: FileInput[] | undefined = undefined;

            if (descriptionImages.status === "fulfilled") {
                newDescriptionImages = descriptionImages.value;
            }

            let newImages: PartialCreateCampaignInquiryInput["images"] | undefined = undefined;

            if (images.status === "fulfilled") {
                newImages = images.value;
            }

            let newProducts: PartialCreateCampaignInquiryInput["products"] | undefined = undefined;

            if (products.status === "fulfilled") {
                newProducts = products.value;
            }

            let newThumbnailImage: FileInput | undefined = undefined;

            if (thumbnailImage.status === "fulfilled") {
                newThumbnailImage = thumbnailImage.value;
            }

            return {
                ...locationData,
                description: {
                    ...locationData?.description,
                    images: newDescriptionImages,
                },
                images: newImages,
                thumbnailImage: newThumbnailImage,
                products: newProducts,
            };
        },
        [createDuplicateImage, createDuplicateImages, createDuplicateCampaignImages, createDuplicateProductsImages]
    );

    return {
        createDuplicateCampaignInquiryImages,
        createDuplicateImage,
        createDuplicateImages,
        createDuplicateCampaignImage,
        createDuplicateProductImages,
        createDuplicateProductsImages,
    };
};
